import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import { useTranslation } from '@hooks';
import { validateFileSize, validateFileParams, formatVideoDuration } from '@helpers/upload-file';
import { handleApiError } from '@helpers/api';
import { alertActions } from '@actions';
import { MEDIA_TYPE } from '@constants/file';
import { CloseIcon, ImportIcon } from 'assets/images/icons';

const Container = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
});

const SortingContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
});

const MediaWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    background: 'var(--action-disabled-background)',
    width: 74,
    height: 74,
    borderRadius: 8,
    padding: 8,
    cursor: 'pointer',
    '& img, video': {
        width: 58,
        borderRadius: 8,
        height: 58,
        display: 'inline-block',
        objectFit: 'contain',
        background: '#fff',
    },
});

const RemoveIconWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    top: 0,
    cursor: 'pointer',
    width: 20,
    height: 20,
    backgroundColor: 'var(--text-secondary)',
    borderRadius: '50%',
    zIndex: 3,
    '& svg': {
        width: '16px',
        '& path': {
            fill: '#fff',
        },
    },
});

const Media = ({ listeners, attributes, item, disabled, onClickMedia, deleteMedia }) => {
    return (
        <MediaWrapper onClick={() => onClickMedia(item.link)}>
            {!disabled ? (
                <RemoveIconWrapper
                    onClick={e => {
                        e.stopPropagation();
                        deleteMedia(item.link);
                    }}
                >
                    <CloseIcon />
                </RemoveIconWrapper>
            ) : null}
            {item.type === MEDIA_TYPE.VIDEO && (
                <span
                    style={{
                        position: 'absolute',
                        color: '#fff',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    <PlayArrowIcon sx={{ width: '28px', height: '28px' }} />
                </span>
            )}
            {item.type === MEDIA_TYPE.IMAGE ? (
                <img style={{ objectFit: 'contain' }} src={item.link} alt="logo" {...listeners} {...attributes} />
            ) : (
                <video width="58" height="58" {...listeners} {...attributes}>
                    <source src={item.link} />
                </video>
            )}
        </MediaWrapper>
    );
};

/**
 * @param {Object} imagesValidation {maxSize, minSize, [minWidth, minHeight, maxWidth, maxHeight]} - max, min size of the picture in bytes, required dimension
 * @param {Object|Function} videoValidation validation rules, or `(mediaCount) => rules` when the
 *   rules depend on how many items the file will sit among (resolved per file in a batch)
 * @param {boolean} disabled disabled state
 * @param {number} limit maximum number of media
 * @param {function} onChange function to update the media list
 * @param {data} list of media links
 * @param {boolean} syncData when true, re-seed internal state if `data` changes externally
 *   after mount (compared by link list). Leave false for callers that don't feed onChange
 *   results back into `data`, or their uploads would be reset.
 * @param {imageServiceCallback} function to upload the picture
 * @param {videoServiceCallback} function to upload the video
 * @param {setIsUploading} function to set the parent uploading state
 */
const MediaUploaderMulti = ({
    limit,
    imagesValidation = { maxSize: undefined, minSize: undefined, dimension: undefined },
    videoValidation = {
        maxSize: undefined,
        minSize: undefined,
        dimension: undefined,
        minDuration: undefined,
        maxDuration: undefined,
    },
    extensions,
    disabled,
    onChange,
    data = [],
    syncData = false,
    imageServiceCallback,
    videoServiceCallback,
    setIsUploading: setIsParentUploading = () => {},
}) => {
    const [media, setMedia] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPercent, setUploadPercent] = useState(null);
    const firstRender = useRef(true);
    // Latest internal media, so the sync effect can compare against it without a stale closure.
    const mediaRef = useRef([]);

    const dispatch = useDispatch();

    useEffect(() => {
        mediaRef.current = media;
    }, [media]);

    useEffect(() => {
        if (firstRender.current) {
            setMedia(data);
            mediaRef.current = data;
            firstRender.current = false;
            return;
        }
        // Opt-in re-seed when the parent replaces `data` out-of-band (e.g. form hydration after
        // mount). Compared by link list so our own onChange echo is a no-op. Off by default for
        // callers that don't feed onChange back into `data` (they'd be reset).
        if (!syncData) return;
        const incoming = data.map(m => m.link).join('|');
        const current = mediaRef.current.map(m => m.link).join('|');
        if (incoming !== current) {
            setMedia(data);
            mediaRef.current = data;
        }
    }, [data, syncData]);

    const { t } = useTranslation();

    const uploadMedia = async e => {
        if (e.target.files.length > limit) {
            dispatch(alertActions.error(t('alertMessages.tooManyFiles', { limit })));
            e.target.value = '';
            return;
        }
        try {
            let files = Array.from(e.target.files);
            if (!files.length) return;

            if (media.length + files.length > limit) {
                files = files.slice(0, limit - media.length);
            }
            // Merged against the ref at write time, so a delete or reorder made during a
            // minutes-long upload is not resurrected by a stale snapshot.
            const appended = [];
            const commit = () => {
                const merged = [
                    ...mediaRef.current,
                    ...appended.filter(a => !mediaRef.current.some(m => m.link === a.link)),
                ];
                setMedia(merged);
                mediaRef.current = merged;
                onChange(merged);
            };

            for (const [index, file] of files.entries()) {
                const mediaType = file.type.startsWith('image') ? MEDIA_TYPE.IMAGE : MEDIA_TYPE.VIDEO;
                // Rules can depend on the item count a file lands in, so they resolve per file
                // assuming the rest of the batch succeeds.
                const fileRules = mediaType === MEDIA_TYPE.IMAGE ? imagesValidation : videoValidation;
                const { maxSize, minSize, dimension, minDuration, maxDuration } =
                    typeof fileRules === 'function'
                        ? fileRules(mediaRef.current.length + appended.length + files.length - index)
                        : fileRules;
                // Size first: it reads metadata only, so oversized files never reach the decode below.
                const { error, metric, size } = validateFileSize(file, maxSize, minSize);
                if (error) {
                    dispatch(alertActions.error(t(`apiErrors.${error}`, { size, metric })));
                    continue;
                }
                let validation;
                if (dimension || minDuration || maxDuration) {
                    const objUrl = URL.createObjectURL(file);
                    try {
                        validation = await validateFileParams(
                            mediaType,
                            objUrl,
                            { dimension, minDuration, maxDuration },
                            reason => {
                                const [minWidth, minHeight, maxWidth, maxHeight] = dimension ?? [];
                                let message = t('apiErrors.invalid_format');
                                if (reason === 'min-dimension' && mediaType === MEDIA_TYPE.IMAGE) {
                                    message = t('apiErrors.image_dimensions_too_small', { minWidth, minHeight });
                                } else if (reason === 'max-dimension') {
                                    message = t('apiErrors.media_dimensions_too_large', { maxWidth, maxHeight });
                                } else if (reason === 'duration') {
                                    message = t('apiErrors.video_duration_out_of_range', {
                                        min: formatVideoDuration(minDuration),
                                        max: formatVideoDuration(maxDuration),
                                    });
                                }
                                dispatch(alertActions.error(message));
                                e.target.value = '';
                            },
                        );
                    } finally {
                        URL.revokeObjectURL(objUrl);
                    }
                    if (!validation.success) {
                        continue;
                    }
                }
                setIsUploading(true);
                setIsParentUploading(true);
                const data = new FormData();
                data.append('file', file);
                data.append('fileName', file.name);
                const response = await (mediaType === MEDIA_TYPE.IMAGE
                    ? imageServiceCallback(data)
                    : videoServiceCallback(data, percent => setUploadPercent(percent)));
                setUploadPercent(null);
                if (!response?.publicUrl) {
                    throw new Error('Upload response contained no file URL');
                }
                appended.push({
                    link: response.publicUrl,
                    type: mediaType,
                    size: file.size,
                    ...(mediaType === MEDIA_TYPE.VIDEO && validation?.duration != null
                        ? { duration: validation.duration, width: validation.width, height: validation.height }
                        : {}),
                });
                commit();
            }
        } catch (error) {
            logger.error("Couldn't upload image");
            handleApiError(error, 'alertMessages.uploadFail');
        }
        setIsUploading(false);
        setIsParentUploading(false);
        setUploadPercent(null);
        e.target.value = '';
    };

    const deleteMedia = link => {
        const updatedMedia = media.filter(i => i.link !== link);
        setMedia(updatedMedia);
        // Synchronous, so an in-flight upload merges against this and not the pre-delete state.
        mediaRef.current = updatedMedia;
        onChange(updatedMedia);
    };

    const onClickMedia = link => window.open(link, '_blank');

    return (
        <Container>
            <DragAndDrop
                items={media.map(i => i.link)}
                data={media}
                strategy="multi"
                onDragEndCb={result => {
                    setMedia(result);
                    mediaRef.current = result;
                    onChange(result);
                }}
            >
                <SortingContainer>
                    {media.map(item => (
                        <SortableItem key={item.link} id={item.link}>
                            <Media
                                item={item}
                                disabled={disabled}
                                onClickMedia={onClickMedia}
                                deleteMedia={deleteMedia}
                            />
                        </SortableItem>
                    ))}
                </SortingContainer>
            </DragAndDrop>
            <div style={{ display: 'flex', flexDirection: 'column', width: 'fit-content', alignItems: 'center' }}>
                <Button
                    variant="outlined"
                    aria-label="upload picture"
                    component="label"
                    disabled={isUploading || disabled || media.length >= limit}
                    startIcon={
                        !isUploading ? (
                            <ImportIcon />
                        ) : (
                            <CircularProgress
                                sx={{ color: 'var(--text-tertiary)' }}
                                size={16}
                                {...(uploadPercent != null ? { variant: 'determinate', value: uploadPercent } : {})}
                            />
                        )
                    }
                >
                    <input
                        hidden
                        accept={extensions ? extensions : 'image/*'}
                        type="file"
                        onChange={uploadMedia}
                        multiple
                    />
                    {isUploading && uploadPercent != null ? `${uploadPercent}%` : t('buttons.upload')}
                </Button>
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'inline-block', marginTop: '2px' }}
                >
                    {media.length}/{limit}
                </Typography>
            </div>
        </Container>
    );
};

export default MediaUploaderMulti;
